import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetManagementContacts } from '#pie/internal/api/pie-bff';
import { FormCombobox } from '../form/form-combobox/FormCombobox';
import type { Props as InputComboboxProps } from '../input-combobox/InputCombobox';
import { Text } from '../text/Text';

interface Props
  extends Omit<
    Partial<ComponentProps<typeof FormCombobox>>,
    'options' | 'name' | 'onLoadMore' | 'totalCount'
  > {
  label: string;
  name?: string;
  contactGroupId: string;
  organisationId?: string;
}

export const FormContactCombobox = ({
  name = '',
  contactGroupId,
  organisationId,
  ...props
}: Props) => {
  const { t } = useTranslation();

  const [options, setOptions] =
    useState<InputComboboxProps['options']>(undefined);
  const [search, setSearch] = useState<string | undefined>(props.defaultValue);
  const [pageIndex, setPageIndex] = useState(0);

  const { data, isLoading } = useGetManagementContacts(
    {
      filter: {},
      pagination: {
        pageIndex,
        pageSize: 20,
      },
      search,
    },
    { query: { enabled: !!organisationId } },
  );

  useEffect(() => {
    const availableContacts = (data?.items ?? []).filter(
      ({ contactGroupMemberships }) =>
        !contactGroupMemberships?.some(
          ({ contactGroup, subunit }) =>
            contactGroup.id === contactGroupId && subunit.id === organisationId,
        ),
    );

    setOptions((currentOptions) =>
      Array.from(
        new Map([
          ...(currentOptions ?? []).map(
            (option) => [option.value, option] as const,
          ),
          ...availableContacts.map(
            (item) =>
              [item.id, { label: item.fullName, value: item.id }] as const,
          ),
        ]).values(),
      ),
    );
  }, [contactGroupId, data, organisationId]);

  const onSearch = (searchTerm: typeof search) => {
    setOptions(undefined);
    setSearch(searchTerm);
    setPageIndex(0);
  };

  const hasNoAvailableContacts =
    !isLoading && !search && data?.totalCount === 0;

  const hasMorePages =
    data && pageIndex * 20 + data.items.length < data.totalCount;

  if (hasNoAvailableContacts) {
    return <Text>{t('cpr_contact_group_detail.add_user.contact.empty')}</Text>;
  }

  return (
    <FormCombobox
      name={name}
      options={options}
      disabled={!organisationId}
      onSearch={onSearch}
      isLoading={isLoading}
      onClear={() => {}}
      shouldFilter={false}
      placeholder={t('cpr_contact_group_detail.add_user.contact.placeholder')}
      onLoadMore={() => setPageIndex((currentPage) => currentPage + 1)}
      totalCount={hasMorePages ? (options?.length ?? 0) + 1 : options?.length}
      {...props}
    />
  );
};
